// @ts-nocheck
/**
 * OrderBySkuContent — Custom grid, SKUs from open order data only
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { REQUESTS } from 'utils/services/thetaretail/requests';
import { putAPIDataV3 } from 'utils/api';
import { removeItemFromLocalStorage } from 'utils/storage';
import { updateSelectedRows } from 'models/dynamicGrid/reducer';
import SkuSelectionBox from './components/skuSelectionBox';
import DynamicFilterBox from 'molecules/dynamicFilterBox/dynamicFilterBox';
import AgOrderGrid from './components/agOrderGrid';
import DistributeModal from './components/distributeModal';
import { Dot9loader } from 'molecules/dot9loader';
import * as obsSagaActions from './sagaActions';
import { fetchBookmarksList } from 'models/bookmarks/sagaActions';
import {
	SELECT_ROW_KEY,
	BULK_ITEMS_PER_PAGE,
	VALIDATION_ERROR_DISPLAY_MS,
	DEBOUNCE_SAVE_MS,
	DEFAULT_EXPORT_FILENAME,
} from './constants';
import { getObsColumns, getObsOrderData, getObsAvailability, getObsLoading } from './selector';
import './orderBySku.scss';

interface OrderBySkuContentProps {
	tableId: number;
	orderStatusValue?: string;
}

const OrderBySkuContent = forwardRef<any, OrderBySkuContentProps>(({ tableId, orderStatusValue = '3' }, ref) => {
	const dispatch = useDispatch();
	const gridApiRef = useRef<any>(null);
	useMemo(() => {
		try {
			removeItemFromLocalStorage(tableId);
		} catch (e) {}
	}, [tableId]);

	/* ─── Direct API save buffer (bypasses saga — reliable even without Redux grid metadata) ─── */
	const bulkBuffer = useRef<Record<string, any>>({});
	const lastEditRow = useRef<any>(null);

	/** Send buffered edits directly to the UPDATE_ROW_DATA API — uses ref to avoid stale closures */
	const flushSaveRef = useRef<(edits: Record<string, any>, refRow: any, orderKey: string) => Promise<void>>();
	flushSaveRef.current = async (edits: Record<string, any>, refRow: any, orderKey: string) => {
		const editValues = Object.values(edits);
		if (!refRow || !editValues.length) {
			console.warn('[OBS] flushSave skipped — no refRow or empty edits');
			return;
		}
		const keyValue = refRow[SELECT_ROW_KEY];
		if (keyValue === undefined || keyValue === null) {
			console.error(`[OBS] CRITICAL: ${SELECT_ROW_KEY} not found in row!`);
			return;
		}
		try {
			/* Build groupSearchParams with OrderID — the saga always includes this
			   for order pages (tableId 1,3,4,5,6) so the backend knows which order context */
			const orderId = refRow['OrderID'];
			const groupSearchParams = orderId != null ? [{ column: 'OrderID', value: orderId, type: 'number' }] : [];

			const body = {
				tableId,
				updatedValue: refRow[orderKey],
				rowIdentifier: orderKey,
				selectedRowKeyName: SELECT_ROW_KEY,
				selectedRowKeyValue: keyValue,
				BulkUpdateJSON: editValues,
				/* Fields the saga normally includes for tableId 1 (isRowUpdateNotificationEnabled=false) */
				isMultiRowUpdate: true,
				pageNumber: 1,
				itemsPerPage: BULK_ITEMS_PER_PAGE,
				groupSearchParams,
				search: JSON.stringify({ filter: [] }),
				isAdvancedSearch: 1,
			};
			const response = await putAPIDataV3({
				service: REQUESTS.PUT.UPDATE_ROW_DATA.service,
				url: REQUESTS.PUT.UPDATE_ROW_DATA.url,
				body,
			});
			const respData = response?.data || response;

			/* ── Handle success: apply returned row data to allOrderDataRef ── */
			const updatedRows = respData?.updatedRows;
			const updateAvlQty = respData?.updateAvlQty;
			if (Array.isArray(updatedRows) && updatedRows.length > 0) {
				// Build a lookup for fast matching: OrderDetailID → returned row
				const returnedMap = new Map<string, any>();
				updatedRows.forEach((r: any) => {
					const id = r[SELECT_ROW_KEY] ?? r['OrderDetailID'];
					if (id != null) returnedMap.set(String(id), r);
				});

				// Merge updateAvlQty rows (availability changes for sibling rows)
				if (Array.isArray(updateAvlQty)) {
					updateAvlQty.forEach((avl: any) => {
						const id = String(avl.RowIdentifier ?? avl[SELECT_ROW_KEY] ?? '');
						if (id && !returnedMap.has(id)) returnedMap.set(id, avl);
					});
				}

				// Apply returned values to allOrderDataRef rows in-place
				const data = allOrderDataRef.current;
				const refreshNodes: any[] = [];
				const gridApi = gridApiRef.current;
				for (let i = 0; i < data.length; i++) {
					const rowId = String(data[i][SELECT_ROW_KEY] ?? data[i]['OrderDetailID'] ?? '');
					const returned = returnedMap.get(rowId);
					if (!returned) continue;
					// Merge each returned field into the master row (except internal keys)
					Object.keys(returned).forEach((key) => {
						if (key === 'RowIdentifier') return;
						const val = returned[key];
						// The saga wraps values as { isUpdated, value } — unwrap if needed
						data[i][key] = val && typeof val === 'object' && val.value !== undefined ? val.value : val;
					});
					// Collect grid node for refresh
					if (gridApi) {
						const node = gridApi.getRowNode(rowId);
						if (node) refreshNodes.push(node);
					}
				}

				// Refresh affected grid cells to display updated values
				if (gridApi && refreshNodes.length > 0) {
					gridApi.refreshCells({ rowNodes: refreshNodes, force: true });
				}
				bumpMetrics();
			}

			// Handle backend validation errors — revert affected cells and flash error
			if (respData?.validationErrors?.length > 0) {
				const gridApi = gridApiRef.current;
				if (gridApi) {
					// Build a Map for O(1) lookups instead of O(N*M) forEachNode per error
					const errorMap = new Map();
					respData.validationErrors.forEach((ve: any) => {
						errorMap.set(String(ve.RowIdentifier), ve.ErrorMessage || 'Server validation failed');
					});
					const affectedNodes: any[] = [];
					gridApi.forEachNode((node: any) => {
						if (node.data && errorMap.has(String(node.data.OrderDetailID))) {
							const errorMsg = errorMap.get(String(node.data.OrderDetailID));
							node.data.OrderQty = 0;
							node.data._availError = errorMsg;
							affectedNodes.push(node);
							// Update master data array
							const masterRow = allOrderDataRef.current.find(
								(r) => String(r.OrderDetailID) === String(node.data.OrderDetailID)
							);
							if (masterRow) masterRow.OrderQty = 0;
						}
					});
					// Batch refresh all affected cells at once
					if (affectedNodes.length > 0) {
						gridApi.refreshCells({ rowNodes: affectedNodes, columns: ['OrderQty'], force: true });
						// Clear errors after 3 seconds
						setTimeout(() => {
							const stillValid: any[] = [];
							affectedNodes.forEach((node) => {
								if (node.data?._availError) {
									delete node.data._availError;
									stillValid.push(node);
								}
							});
							if (stillValid.length > 0) {
								try {
									gridApi.refreshCells({ rowNodes: stillValid, columns: ['OrderQty'], force: true });
								} catch (e) {}
							}
						}, VALIDATION_ERROR_DISPLAY_MS);
					}
					bumpMetrics();
				}
			}
		} catch (e: any) {
			console.error('[OBS] Save FAILED:', e?.message || e, e?.response?.data || '');
		}
	};

	const debouncedSendBulk = useRef(
		_.debounce(() => {
			const row = lastEditRow.current;
			if (!row || !Object.keys(bulkBuffer.current).length) return;
			const orderKey = Object.keys(row).find((k) => /^orderqty$/i.test(k)) || 'OrderQty';
			flushSaveRef.current?.({ ...bulkBuffer.current }, row, orderKey);
			bulkBuffer.current = {};
		}, DEBOUNCE_SAVE_MS)
	).current;

	/* Cleanup debounce on unmount */
	useEffect(() => {
		return () => {
			if (debouncedSendBulk?.cancel) debouncedSendBulk.cancel();
		};
	}, [debouncedSendBulk]);

	/* 1) Columns → SKU / Store / Warehouse (via saga) */
	useEffect(() => {
		dispatch(obsSagaActions.fetchOrderColumns({ tableId }));
	}, [tableId]);
	/* 1a) Load bookmarks for sidebar panel */
	useEffect(() => {
		dispatch(fetchBookmarksList({ tableId: String(tableId) }));
	}, [tableId]);
	const { skuColumns, storeColumns, warehouseColumns, orderColumns } = useSelector(getObsColumns);

	const [skuIndex, setSkuIndex] = useState(0);

	const [skuActiveKeys, setSkuActiveKeys] = useState<string[]>([]);
	const [skuFilterValues, setSkuFilterValues] = useState<Record<string, string>>({});
	const [storeActiveKeys, setStoreActiveKeys] = useState<string[]>([]);
	const [storeFilterValues, setStoreFilterValues] = useState<Record<string, string>>({});
	const [whActiveKeys, setWhActiveKeys] = useState<string[]>([]);
	const [whFilterValues, setWhFilterValues] = useState<Record<string, string>>({});
	const [orderActiveKeys, setOrderActiveKeys] = useState<string[]>([]);
	const [orderFilterValues, setOrderFilterValues] = useState<Record<string, string>>({});

	const [massMetric, setMassMetric] = useState('flat');
	const [massBaseQty, setMassBaseQty] = useState(0);
	const [distributeOpen, setDistributeOpen] = useState(false);
	const [overflowOpen, setOverflowOpen] = useState(false);
	const [overflowInfo, setOverflowInfo] = useState<any>(null);

	/* 2) Fetch order data — progressive loading via saga, refresh via refreshKey */
	const [refreshKey, setRefreshKey] = useState(0);
	useEffect(() => {
		dispatch(obsSagaActions.fetchOrderData({ tableId, orderStatusValue, refreshKey }));
	}, [tableId, orderStatusValue, refreshKey]);
	const obsOrderData = useSelector(getObsOrderData);
	const obsLoading = useSelector(getObsLoading);
	const dataLoading = obsLoading.data;
	const backgroundLoading = obsLoading.backgroundData;

	/* Mutable ref for in-place cell edits + version counters for re-render triggers */
	const allOrderDataRef = useRef<any[]>([]);
	const [dataVersion, setDataVersion] = useState(0);
	const [metricsVersion, setMetricsVersion] = useState(0);
	const bumpData = useCallback(() => {
		setDataVersion((v) => v + 1);
		setMetricsVersion((v) => v + 1);
	}, []);
	const bumpMetrics = useCallback(() => setMetricsVersion((v) => v + 1), []);

	/* Sync mutable ref from Redux state whenever saga updates it.
	   Shallow-copy each row so AG Grid can mutate cell values in place
	   (Redux/Immer freezes objects in the store, making them read-only). */
	useEffect(() => {
		if (obsOrderData.rows.length > 0) {
			allOrderDataRef.current = obsOrderData.rows.map((r) => ({ ...r }));
			bumpData();
		}
	}, [obsOrderData.rows]);
	const allOrderData = allOrderDataRef.current;

	/* ─── Expose refresh / resetView to parent via ref ─── */
	useImperativeHandle(
		ref,
		() => ({
			refresh: () => {
				/* Re-run data fetch by bumping refreshKey */
				setRefreshKey((k) => k + 1);
			},
			resetView: () => {
				/* Clear AG Grid column state, filters, and sort */
				if (gridApiRef.current) {
					gridApiRef.current.resetColumnState();
					gridApiRef.current.setFilterModel(null);
					gridApiRef.current.applyColumnState({ defaultState: { sort: null } });
				}
			},
			exportData: (fileName?: string) => {
				if (gridApiRef.current) {
					gridApiRef.current.exportDataAsCsv({
						fileName: fileName || DEFAULT_EXPORT_FILENAME,
						columnKeys: gridApiRef.current
							.getAllDisplayedColumns()
							?.filter((c) => c.getColId() !== 'ag-Grid-SelectionColumn')
							.map((c) => c.getColId()),
					});
				}
			},
		}),
		[]
	);

	/* ─── Sync row selection to Redux so ActionBar modals can access it ─── */
	const handleSelectionChanged = useCallback(
		(selectedRows: any[]) => {
			const ids = selectedRows.map((r) => r.OrderDetailID).filter(Boolean);
			dispatch(
				updateSelectedRows({
					tableId,
					data: { selectedRowKeys: ids, isSelectAll: false, rowGroup: null },
				})
			);
		},
		[tableId, dispatch]
	);

	/* 3) SKU list derived from order data */
	const skuFieldName = useMemo(() => {
		if (!allOrderData.length) return 'SKU';
		return Object.keys(allOrderData[0] || {}).find((k) => /^sku$/i.test(k)) || 'SKU';
	}, [dataVersion]);

	const skuDescFieldName = useMemo(() => {
		if (!allOrderData.length) return '';
		const keys = Object.keys(allOrderData[0] || {});
		return keys.find((k) => /^skudesc/i.test(k)) || keys.find((k) => /sku.*desc/i.test(k)) || '';
	}, [dataVersion]);

	/* 3a) Filtering: SKU + Store + Warehouse cross-filters (moved before skuList so SKU count is cross-filtered) */
	const applyFilters = useCallback((data, filterVals) => {
		const active = Object.entries(filterVals).filter(([, v]) => v && v !== 'all');
		if (!active.length) return data;
		return data.filter((row) => active.every(([key, val]) => String(row[key] || '') === val));
	}, []);

	const allFilters = useMemo(
		() => ({ ...skuFilterValues, ...storeFilterValues, ...whFilterValues, ...orderFilterValues }),
		[skuFilterValues, storeFilterValues, whFilterValues, orderFilterValues]
	);
	const fullyFilteredData = useMemo(() => applyFilters(allOrderData, allFilters), [dataVersion, allFilters]);
	const dataForSkuFilter = useMemo(
		() => applyFilters(allOrderData, { ...storeFilterValues, ...whFilterValues, ...orderFilterValues }),
		[dataVersion, storeFilterValues, whFilterValues, orderFilterValues]
	);
	const dataForStoreFilter = useMemo(
		() => applyFilters(allOrderData, { ...skuFilterValues, ...whFilterValues, ...orderFilterValues }),
		[dataVersion, skuFilterValues, whFilterValues, orderFilterValues]
	);
	const dataForWhFilter = useMemo(
		() => applyFilters(allOrderData, { ...skuFilterValues, ...storeFilterValues, ...orderFilterValues }),
		[dataVersion, skuFilterValues, storeFilterValues, orderFilterValues]
	);
	const dataForOrderFilter = useMemo(
		() => applyFilters(allOrderData, { ...skuFilterValues, ...storeFilterValues, ...whFilterValues }),
		[dataVersion, skuFilterValues, storeFilterValues, whFilterValues]
	);

	/* ─── SKU sort state ─── */
	const [skuSortField, setSkuSortField] = useState<string>('');

	/* Build the set of SKU-level attribute column names for enrichment */
	const skuAttrFields = useMemo(() => {
		if (!skuColumns.length) return [] as string[];
		return skuColumns.map((c) => c.columnName);
	}, [skuColumns]);

	/* 3b) SKU list derived from cross-filtered data (store + warehouse filters applied)
	 *     Now enriched with all SKU-level attributes for sorting/grouping */
	const skuList = useMemo(() => {
		const source = dataForSkuFilter.length ? dataForSkuFilter : allOrderData;
		if (!source.length) return [];
		const seen = new Map();
		source.forEach((row) => {
			const sk = String(row[skuFieldName] || '');
			if (sk && !seen.has(sk)) {
				// Carry all SKU-level attributes from the first row of each SKU
				const skuObj: Record<string, any> = { [skuFieldName]: sk };
				if (skuDescFieldName) skuObj[skuDescFieldName] = row[skuDescFieldName] || '';
				skuAttrFields.forEach((f) => {
					if (row[f] !== undefined) skuObj[f] = row[f];
				});
				seen.set(sk, skuObj);
			}
		});
		const list = Array.from(seen.values());

		// Apply sort
		if (skuSortField) {
			list.sort((a, b) => {
				const va = String(a[skuSortField] || '').toLowerCase();
				const vb = String(b[skuSortField] || '').toLowerCase();
				// Natural sort: try numeric first, fall back to string
				const na = parseFloat(va),
					nb = parseFloat(vb);
				if (!isNaN(na) && !isNaN(nb)) return na - nb;
				return va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' });
			});
		}

		return list;
	}, [dataVersion, skuFieldName, skuDescFieldName, skuAttrFields, dataForSkuFilter, skuSortField]);

	const currentSku = skuList[skuIndex] || null;
	const skuId = currentSku ? String(currentSku[skuFieldName] || '') : '';

	const prevSkuIdRef = useRef<string>('');
	useEffect(() => {
		if (!skuList.length) return;
		// Try to stay on the same SKU after filter change
		if (prevSkuIdRef.current) {
			const newIdx = skuList.findIndex((s) => String(s[skuFieldName] || '') === prevSkuIdRef.current);
			if (newIdx >= 0 && newIdx !== skuIndex) {
				setSkuIndex(newIdx);
				return;
			}
		}
		if (skuIndex >= skuList.length) setSkuIndex(0);
	}, [skuList]);

	// Track current SKU id for re-selection after filter changes
	useEffect(() => {
		prevSkuIdRef.current = skuId;
	}, [skuId]);

	const handleSkuNavigate = useCallback(
		(dir) => {
			const next = skuIndex + dir;
			if (next >= 0 && next < skuList.length) setSkuIndex(next);
		},
		[skuIndex, skuList.length]
	);

	/* 4) Grid data for selected SKU — pass original row refs (shared with allOrderDataRef) */
	const skuOrderData = useMemo(() => {
		if (!skuId || !allOrderData.length) return [];
		const rows = fullyFilteredData.filter((row) => String(row[skuFieldName] || '') === skuId);
		return rows;
	}, [skuId, fullyFilteredData, skuFieldName]);

	/* All rows for selected SKU (unfiltered) — used by DistributeModal for attribute discovery */
	const allSkuOrderData = useMemo(() => {
		if (!skuId || !allOrderData.length) return [];
		return allOrderData.filter((row) => String(row[skuFieldName] || '') === skuId);
	}, [skuId, allOrderData, skuFieldName]);

	/* 6) Availability — fetch from availability API per SKU (via saga) */
	useEffect(() => {
		dispatch(obsSagaActions.fetchSkuAvailability({ skuId }));
	}, [skuId]);
	const skuAvailData = useSelector(getObsAvailability);

	/* Detect the shelf-qty field name from order data (per-row, not a single global value) */
	const shelfQtyField = useMemo(() => {
		if (!allOrderData.length) return 'QtyPerShelf';
		const keys = Object.keys(allOrderData[0]);
		return keys.find((k) => /^qtypershelf$/i.test(k))
			|| keys.find((k) => /^ordermultiple$/i.test(k))
			|| 'QtyPerShelf';
	}, [dataVersion]);

	const skuAvailInfo = useMemo(() => {
		const initialAvail = Number(skuAvailData?.Initial_Available_Qty || 0);
		const currentAvail = Number(skuAvailData?.Current_Available_Qty || 0);
		// Use initialAvail as the effective availability — Current_Available_Qty
		// has data errors in dev; Initial_Available_Qty is the reliable source.
		const effectiveAvail = initialAvail;
		// Sum ordered qty directly from the ref (not skuOrderData) so we can update without recomputing grid data
		if (!skuId || !allOrderDataRef.current.length)
			return { initialAvail: effectiveAvail, currentAvail, orderedQty: 0, remainQty: effectiveAvail };
		const keys = Object.keys(allOrderDataRef.current[0]);
		const orderKey = keys.find((k) => /^orderqty$/i.test(k)) || keys.find((k) => /order.*qty/i.test(k));
		let totalOrdered = 0;
		allOrderDataRef.current.forEach((row) => {
			if (String(row[skuFieldName] || '') === skuId) {
				totalOrdered += Number(row[orderKey] || 0);
			}
		});
		return {
			initialAvail: effectiveAvail,
			currentAvail,
			orderedQty: totalOrdered,
			remainQty: effectiveAvail - totalOrdered,
		};
	}, [metricsVersion, skuAvailData, skuId, skuFieldName]);

	/* 7) Filter handlers */
	const applyDynamicFilter = useCallback(() => {}, []);
	const mkToggle = (setter, valSetter) => (key) => {
		setter((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
		valSetter((prev) => {
			const n = { ...prev };
			delete n[key];
			return n;
		});
	};
	const handleSkuToggle = useCallback(mkToggle(setSkuActiveKeys, setSkuFilterValues), []);
	const handleStoreToggle = useCallback(mkToggle(setStoreActiveKeys, setStoreFilterValues), []);
	const handleWhToggle = useCallback(mkToggle(setWhActiveKeys, setWhFilterValues), []);
	const handleOrderToggle = useCallback(mkToggle(setOrderActiveKeys, setOrderFilterValues), []);

	/**
	 * Mass-update helper: replaces affected rows in allOrderDataRef with NEW objects
	 * so that AG Grid (using getRowId) detects the data change by reference.
	 * Individual cell edits mutate in place — this is only for bulk operations.
	 */
	const massUpdateOrderQty = useCallback(
		(updateFn: (row: any) => number | null) => {
			const orderKey =
				Object.keys(allOrderDataRef.current[0] || {}).find((k) => /^orderqty$/i.test(k)) || 'OrderQty';
			const bulkEdits: { RowIdentifier: any; OrderQty: number }[] = [];
			let lastRow = null;

			allOrderDataRef.current = allOrderDataRef.current.map((row) => {
				if (String(row[skuFieldName]) !== skuId) return row; // not this SKU, keep same ref
				const newQty = updateFn(row);
				if (newQty === null) return row; // no change
				const updated = { ...row, [orderKey]: newQty }; // new object so AG Grid sees it
				bulkEdits.push({ RowIdentifier: row['OrderDetailID'], OrderQty: newQty });
				lastRow = updated;
				return updated;
			});

			// ─── Send bulk save directly to API ───
			if (bulkEdits.length > 0 && lastRow) {
				// Flush any pending debounced single-cell edits first
				if (debouncedSendBulk?.cancel) debouncedSendBulk.cancel();
				bulkBuffer.current = {};

				// Build keyed map for flushSave
				const editsMap: Record<string, any> = {};
				bulkEdits.forEach((e) => {
					editsMap[e.RowIdentifier] = e;
				});
				flushSaveRef.current?.(editsMap, lastRow, orderKey);
			}

			bumpData();
		},
		[skuId, skuFieldName, bumpData, debouncedSendBulk]
	);

	/** Resolve per-row shelf step for a given data row */
	const getRowStep = useCallback(
		(row: any) => {
			if (shelfQtyField && row) {
				const v = Number(row[shelfQtyField] || 0);
				if (v > 1) return v;
			}
			return 1;
		},
		[shelfQtyField]
	);

	const handleApplyMass = useCallback(() => {
		if (!skuId || !allOrderData.length) return;
		if (massMetric === 'flat') {
			// Zero out — always allow
			if (massBaseQty <= 0) {
				massUpdateOrderQty(() => 0);
				return;
			}

			// Pre-calculate per-row snapped totals to check availability
			const skuRows = allOrderData.filter((r) => String(r[skuFieldName]) === skuId);
			let totalIfApplied = 0;
			skuRows.forEach((row) => {
				const step = getRowStep(row);
				totalIfApplied += Math.ceil(massBaseQty / step) * step;
			});
			const availCap = skuAvailInfo.initialAvail;

			if (totalIfApplied > availCap) {
				setOverflowInfo({ qty: massBaseQty, storeCount: skuRows.length, total: totalIfApplied, avail: availCap });
				setOverflowOpen(true);
				return;
			}

			// Apply with per-row snapping
			massUpdateOrderQty((row) => {
				const step = getRowStep(row);
				return Math.ceil(massBaseQty / step) * step;
			});
		}
	}, [massMetric, massBaseQty, skuId, skuFieldName, dataVersion, getRowStep, skuAvailInfo, massUpdateOrderQty]);

	const handleOverflowResolve = useCallback(
		(action: 'proportional' | 'cap' | 'cancel') => {
			if (!overflowInfo || action === 'cancel') {
				setOverflowOpen(false);
				return;
			}
			const { avail, storeCount } = overflowInfo;
			const avgPerStore = Math.floor(avail / storeCount);

			// Apply scaled qty with per-row snapping
			massUpdateOrderQty((row) => {
				const step = getRowStep(row);
				return Math.floor(avgPerStore / step) * step;
			});
			setOverflowOpen(false);
		},
		[overflowInfo, massUpdateOrderQty, getRowStep]
	);

	const handleOrderQtyChange = useCallback(
		(rowIdx: number, storeId: string, newQty: number) => {
			if (!allOrderData.length) return;
			const orderKey = Object.keys(allOrderData[0]).find((k) => /^orderqty$/i.test(k)) || 'OrderQty';
			const storeKey = Object.keys(allOrderData[0]).find((k) => /^store$/i.test(k)) || 'Store';

			// The grid already wrote the snapped/capped value to data.OrderQty.
			// Ensure allOrderDataRef is in sync (same object refs, but just in case).
			const data = allOrderDataRef.current;
			let editedRow = null;
			for (let i = 0; i < data.length; i++) {
				if (String(data[i][skuFieldName]) === skuId && String(data[i][storeKey]) === storeId) {
					data[i][orderKey] = newQty;
					editedRow = data[i];
					break;
				}
			}

			// ─── Buffer edit for debounced API save ───
			if (editedRow) {
				const rowId = editedRow['OrderDetailID'];
				const existing = bulkBuffer.current[rowId] || { RowIdentifier: rowId };
				bulkBuffer.current[rowId] = { ...existing, OrderQty: newQty };
				lastEditRow.current = editedRow;
				debouncedSendBulk();
			}

			// Only bump metrics (sidebar totals) — do NOT recompute skuOrderData/rowData
			bumpMetrics();
		},
		[dataVersion, skuId, skuFieldName, bumpMetrics, debouncedSendBulk]
	);

	const isDistribute = massMetric === 'distribute';
	const canApply = massBaseQty >= 0; // Allow zero for flat

	/* Memoize groupableColumns so AgOrderGrid doesn't re-render on every parent render */
	const groupableColumns = useMemo(
		() => [...storeColumns, ...warehouseColumns].map((c) => ({ key: c.columnName, label: c.displayName })),
		[storeColumns, warehouseColumns]
	);

	// Handle distribute modal apply — receives { storeId: qty }
	const handleDistributeApply = useCallback(
		(storeQtyMap: Record<string, number>) => {
			if (!allOrderData.length) return;
			const storeKey = Object.keys(allOrderData[0]).find((k) => /^store$/i.test(k)) || 'Store';
			massUpdateOrderQty((row) => {
				const store = String(row[storeKey] || '');
				return store in storeQtyMap ? storeQtyMap[store] : null;
			});
		},
		[dataVersion, skuId, skuFieldName, massUpdateOrderQty]
	);

	return (
		<div className="obs-bysku-wrapper">
			{dataLoading && <Dot9loader overlay message="Loading order data…" />}
			{backgroundLoading && <div className="obs-bg-loading-bar">Loading remaining records…</div>}
			<div className="obs-split-layout">
				<div className="obs-grid-area">
					<AgOrderGrid
						data={skuOrderData}
						onOrderQtyChange={handleOrderQtyChange}
						onSelectionChanged={handleSelectionChanged}
						gridApiRef={gridApiRef}
						groupableColumns={groupableColumns}
						skuAvailQty={skuAvailInfo.initialAvail}
						shelfQtyField={shelfQtyField}
						skuLabel={skuId}
						skuDesc={currentSku?.[skuDescFieldName] || ''}
						skuOrdTotal={skuAvailInfo.orderedQty}
						skuRemain={skuAvailInfo.remainQty}
					/>
				</div>
				<div className="obs-right-panel">
					<SkuSelectionBox
						skuList={skuList}
						skuIndex={skuIndex}
						onNavigate={handleSkuNavigate}
						onSelectIndex={(idx) => setSkuIndex(idx)}
						loading={dataLoading}
						availInfo={skuAvailInfo}
						sortOptions={skuColumns}
						sortField={skuSortField}
						onSortChange={setSkuSortField}
					/>
					{skuColumns.length > 0 && (
						<DynamicFilterBox
							title="Filter SKUs"
							icon="🏷"
							accentColor="#7b1fa2"
							columns={skuColumns}
							gridData={dataForSkuFilter}
							activeKeys={skuActiveKeys}
							onToggleKey={handleSkuToggle}
							filterValues={skuFilterValues}
							onValueChange={(k, v) => setSkuFilterValues((p) => ({ ...p, [k]: v }))}
							matchLabel="SKUs"
							onApplyFilter={applyDynamicFilter}
						/>
					)}
					{storeColumns.length > 0 && (
						<DynamicFilterBox
							title="Filter Stores"
							icon="🏪"
							accentColor="#1565c0"
							columns={storeColumns}
							gridData={dataForStoreFilter}
							activeKeys={storeActiveKeys}
							onToggleKey={handleStoreToggle}
							filterValues={storeFilterValues}
							onValueChange={(k, v) => setStoreFilterValues((p) => ({ ...p, [k]: v }))}
							matchLabel="Stores"
							onApplyFilter={applyDynamicFilter}
						/>
					)}
					{warehouseColumns.length > 0 && (
						<DynamicFilterBox
							title="Filter Warehouse"
							icon="🏭"
							accentColor="#e65100"
							columns={warehouseColumns}
							gridData={dataForWhFilter}
							activeKeys={whActiveKeys}
							onToggleKey={handleWhToggle}
							filterValues={whFilterValues}
							onValueChange={(k, v) => setWhFilterValues((p) => ({ ...p, [k]: v }))}
							matchLabel="Warehouses"
							onApplyFilter={applyDynamicFilter}
						/>
					)}
					{orderColumns.length > 0 && (
						<DynamicFilterBox
							title="Filter Orders"
							icon="📋"
							accentColor="#f57c00"
							columns={orderColumns}
							gridData={dataForOrderFilter}
							activeKeys={orderActiveKeys}
							onToggleKey={handleOrderToggle}
							filterValues={orderFilterValues}
							onValueChange={(k, v) => setOrderFilterValues((p) => ({ ...p, [k]: v }))}
							matchLabel="Orders"
							onApplyFilter={applyDynamicFilter}
						/>
					)}
					<div className="obs-box obs-box--mass">
						<div className="obs-box__title">⚡ Smart Mass Order</div>
						<div className="obs-mass__field">
							<div className="obs-label">Order Method</div>
							<select
								value={massMetric}
								onChange={(e) => {
									setMassMetric(e.target.value);
									if (e.target.value === 'distribute') setDistributeOpen(true);
								}}
								className="obs-box__select"
							>
								<option value="flat">Flat — Same qty to all stores</option>
								<option value="distribute">Distribute by Attribute</option>
							</select>
						</div>
						{isDistribute && (
							<div className="obs-mass__actions">
								<button
									onClick={() => setDistributeOpen(true)}
									className="obs-mass__apply-btn obs-mass__apply-btn--active"
								>
									Open Distribution Setup
								</button>
							</div>
						)}
						{!isDistribute && (
							<>
								<div className="obs-mass__inputs">
									<div>
										<div className="obs-label">
											Qty per Store (snaps to each store's shelf qty)
										</div>
										<input
											type="number"
											min={0}
											step={1}
											value={massBaseQty}
											onChange={(e) => setMassBaseQty(Number(e.target.value) || 0)}
											className="obs-box__input"
										/>
									</div>
								</div>
								<div className="obs-mass__actions">
									<button
										onClick={handleApplyMass}
										disabled={!canApply}
										className={`obs-mass__apply-btn ${canApply ? 'obs-mass__apply-btn--active' : ''}`}
									>
										Apply to All Stores
									</button>
									<button onClick={() => setMassBaseQty(0)} className="obs-mass__clear-btn">
										Clear
									</button>
								</div>
							</>
						)}
					</div>

					{/* Distribute by Attribute Modal */}
					<DistributeModal
						open={distributeOpen}
						onClose={() => setDistributeOpen(false)}
						skuOrderData={skuOrderData}
						allSkuData={allOrderData}
						skuFieldName={skuFieldName}
						skuId={skuId}
						initialAvail={skuAvailInfo.initialAvail}
						shelfQtyField={shelfQtyField}
						onApply={handleDistributeApply}
						storeColumns={storeColumns}
					/>

					{/* Flat overflow modal */}
					{overflowOpen && overflowInfo && (
						<div className="obs-modal-overlay" onClick={() => setOverflowOpen(false)}>
							<div className="obs-modal" onClick={(e) => e.stopPropagation()}>
								<div className="obs-modal__header">
									<span className="obs-modal__title">Availability Exceeded</span>
									<button className="obs-modal__close" onClick={() => setOverflowOpen(false)}>
										✕
									</button>
								</div>
								<div className="obs-modal__body">
									<div className="obs-modal__overflow-banner">
										Flat {overflowInfo.qty}/store × {overflowInfo.storeCount} stores ={' '}
										<strong>{overflowInfo.total.toLocaleString()}</strong> exceeds availability of{' '}
										<strong>{overflowInfo.avail.toLocaleString()}</strong>.
									</div>
									<div className="obs-modal__subtitle">How would you like to resolve this?</div>
									<div className="obs-modal__overflow-options">
										<label
											className="obs-modal__overflow-opt obs-modal__overflow-opt--sel"
											onClick={() => handleOverflowResolve('proportional')}
										>
											<div>
												<div className="obs-modal__opt-title">Scale to Fit</div>
												<div className="obs-modal__opt-desc">
													Reduce to ~{Math.floor(overflowInfo.avail / overflowInfo.storeCount)}/store
													(snapped to each store's shelf qty).
												</div>
											</div>
										</label>
										<label
											className="obs-modal__overflow-opt"
											onClick={() => setOverflowOpen(false)}
										>
											<div>
												<div className="obs-modal__opt-title">Cancel</div>
												<div className="obs-modal__opt-desc">
													Go back and adjust the quantity manually.
												</div>
											</div>
										</label>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
});

export default OrderBySkuContent;
